import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Zap, MessageSquare, Target, Sparkles, Lock, CheckCircle, Unlock, Loader2, Check, Star, X, Clock, ExternalLink, Phone, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { apiUrl, WEB_URL } from '../api/config';
import api from '../services/api';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { BillingDetailsModal } from '@/components/BillingDetailsModal';
import { getPortalType, getUserData } from '@/utils/auth';
import { initiateCheckout, loadRazorpay } from '@/utils/checkout';
import { useCompliance } from '@/context/ComplianceContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { GoogleAuthButton } from '@/components/auth/GoogleAuthButton';
import { AppleAuthButton } from '@/components/auth/AppleAuthButton';
import { cn } from '@/lib/utils';
import { trackEvent, attachUserProps } from '@/analytics/analytics';

export const formatPrice = (price, currency, fractionDigits = 0) => {
  const symbols = {
    'INR': '₹',
    'SAR': 'SR',
    'QAR': 'QR',
    'AED': 'AED',
    'KWD': 'KD',
    'BHD': 'BD',
    'OMR': 'RO',
    'GBP': '£',
    'USD': '$'
  };

  try {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    const symbol = symbols[currency];

    // For specific currencies like BHD, OMR, KWD, 3 decimal places are common
    const decimals = ['KWD', 'BHD', 'OMR'].includes(currency) ? 3 : fractionDigits;

    if (symbol) {
      const formattedNum = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals === 0 ? 2 : decimals,
      }).format(numPrice || 0);
      return `${symbol} ${formattedNum}`;
    }

    return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals === 0 ? 2 : decimals,
    }).format(numPrice || 0);
  } catch (e) {
    const symbol = symbols[currency] || currency || '$';
    return `${symbol} ${price || 0}`;
  }
};

/** Build dynamic benefit lines from a plan's feature limits */
export const buildPlanBenefits = (plan) => {
  if (!plan) return { preTitle: '', listHeading: '', points: [] };
  const description = plan.description || plan.plan_description || '';
  const parts = description.split(':');

  // First part: Title prefix (e.g., "Basic plan for learners")
  const preTitle = parts[0]?.trim() || '';
  // Second part: List heading (e.g., "What's included")
  const listHeading = parts[1]?.trim() || '';
  // Third part onwards: Bullet points
  const pointsRaw = parts.slice(2).join(':').trim() || '';

  const points = pointsRaw.split(/[·•\n]|\s+\.\s+/).map(s => s.trim()).filter(Boolean);

  return { preTitle, listHeading, points };
};

/**
 * Unified Rate Limit & Subscription Dialog
 * Shows when guest users hit rate limits on any feature (402 or 429 errors)
 * Also shows for free users hitting limits
 * Dark mode with compelling messaging about limitations and account benefits
 * 
 * @param {boolean} isOpen - Whether the dialog is open
 * @param {function} onClose - Callback when dialog is closed
 * @param {string} feature - Feature name that triggered the limit
 * @param {number} errorType - HTTP error code (402 for guest limits, 429 for rate limits)
 * @param {boolean} isFreeUser - Whether the user is authenticated on free plan
 * @param {string} mode - Mode of the dialog ('limit' or 'upgrade')
 * @param {boolean} isReactivation - Whether this is a plan reactivation
 */
export default function AuthPromptDialog({ isOpen, onClose, feature = 'this feature', errorType = 402, isFreeUser = false, mode = 'limit', isReactivation = false, showCloseButton = true, portal: portalProp, userType: userTypeProp }) {
  const navigate = useNavigate();
  const location = useLocation();
  const detectedPortal = portalProp || (userTypeProp ? userTypeProp.toLowerCase() : getPortalType(location.pathname));
  const portal = detectedPortal;
  const { show_subscriptions } = useCompliance();

  const [profileUserType, setProfileUserType] = useState(null);
  const isTeacher = portal === 'teacher' ||
    userTypeProp?.toLowerCase() === 'teacher' ||
    profileUserType === 'teacher' ||
    getUserData(portal)?.user_type === 'teacher' ||
    getUserData('teacher')?.user_type === 'teacher';

  const [effectiveIsFreeUser, setEffectiveIsFreeUser] = useState(isFreeUser);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [userSubscription, setUserSubscription] = useState(null);
  const [planCurrency, setPlanCurrency] = useState('USD');
  const [showConfirmSub, setShowConfirmSub] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [rpayEnabled, setRpayEnabled] = useState(false);
  const [mockEnabled, setMockEnabled] = useState(false);

  // State for consolidated subscription modal functionality
  const [activeCycle, setActiveCycle] = useState('monthly');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [freePlanMetadata, setFreePlanMetadata] = useState({
    name: 'Basic',
    description: isTeacher ? 'Basic features for educators.' : 'Basic features for learners.'
  });
  const [upgradingPlanId, setUpgradingPlanId] = useState(null);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [hasParentWhatsapp, setHasParentWhatsapp] = useState(false);

  useEffect(() => {
    setFreePlanMetadata({
      name: 'Basic',
      description: isTeacher ? 'Basic features for educators.' : 'Basic features for learners.'
    });
  }, [isTeacher]);

  const emitAnalyticsEvent = (name, extra = {}) => {
    try {
      trackEvent(name, attachUserProps({
        page: location.pathname,
        feature,
        mode,
        error_type: errorType,
        is_free_user: effectiveIsFreeUser,
        ...extra,
      }));
    } catch { }
  };

  const closePrompt = (reason = 'dismissed') => {
    emitAnalyticsEvent('paywall_dismissed', { dismiss_reason: reason });
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && Number(errorType) !== 429 && errorType !== 'content_mismatch') {
        closePrompt('escape_key');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, errorType, onClose]);

  const openBillingModal = (reason = 'billing_required', extra = {}) => {
    emitAnalyticsEvent('billing_details_requested', { reason, ...extra });
    setShowBillingModal(true);
  };

  useEffect(() => {
    if (isOpen) {
      const isActuallyGuest = localStorage.getItem('is_guest_session') === 'true';
      const hasToken = !!(
        localStorage.getItem('student_token') ||
        localStorage.getItem('teacher_token') ||
        localStorage.getItem('access_token')
      );

      const isSigned = isFreeUser || (hasToken && !isActuallyGuest);
      setEffectiveIsFreeUser(isSigned);

      fetchPlans();
      if (isSigned) {
        fetchUserSubscription();
      }
      try {
        trackEvent('paywall_viewed', attachUserProps({
          page: location.pathname,
          feature,
          error_type: errorType,
          mode,
          is_free_user: isSigned,
        }));
      } catch { }
    }
  }, [isOpen, isFreeUser, mode, errorType, feature, location.pathname]);

  const fetchUserSubscription = async () => {
    try {
      const response = await api.get('users/subscription/');
      setUserSubscription(response.data);
      if (response.data.coupon_code) {
        setCouponError(`You have already used a coupon (${response.data.coupon_code}). One user can use any coupon only once.`);
      }

      try {
        const profRes = await api.get('users/profile/');
        if (profRes.data?.user_type) {
          setProfileUserType(profRes.data.user_type);
        }
        if (!isTeacher && (profRes.data?.phone_no || profRes.data?.parent_whatsapp_number || profRes.data?.has_parent_whatsapp)) {
          setHasParentWhatsapp(true);
        } else {
          setHasParentWhatsapp(false);
        }
      } catch (pErr) {
        console.warn('[AuthPromptDialog] Could not fetch profile:', pErr);
      }
    } catch (err) {
      console.warn('[AuthPromptDialog] Could not fetch user subscription:', err);
    }
  };

  const fetchPlans = async () => {
    try {
      setLoadingPlans(true);
      const userType = (isTeacher ? 'TEACHER' : (userTypeProp || portal || 'STUDENT')).toUpperCase();
      const response = await api.get(`superadmin/subscriptions/pricing/?plan_type=${userType}`);
      const data = response.data;

      if (data.detected_currency) {
        setPlanCurrency(data.detected_currency);
      }
      if (data.stripe_enabled !== undefined) {
        setStripeEnabled(data.stripe_enabled);
      }
      if (data.rpay_enabled !== undefined) {
        setRpayEnabled(data.rpay_enabled);
      }
      if (data.mock_payment_gw_enabled !== undefined) {
        setMockEnabled(data.mock_payment_gw_enabled);
      }

      const rawPlans = data.plans || [];
      setPlans(rawPlans);
      emitAnalyticsEvent('pricing_options_loaded', {
        plan_count: rawPlans.length,
        available_cycles: [
          rawPlans.some(plan => plan.is_monthly_active && plan.monthly_price > 0) ? 'monthly' : null,
          rawPlans.some(plan => plan.is_three_month_active && plan.three_month_price > 0) ? 'quarterly' : null,
          rawPlans.some(plan => plan.is_six_month_active && plan.six_month_price > 0) ? 'half_yearly' : null,
          rawPlans.some(plan => plan.is_yearly_active && plan.yearly_price > 0) ? 'yearly' : null,
          rawPlans.some(plan => plan.is_weekly_active && plan.weekly_price > 0) ? 'weekly' : null,
        ].filter(Boolean),
      });

      // Set default cycle to the first available one on the first plan
      if (rawPlans.length > 0) {
        const p = rawPlans[0];
        if (p.is_monthly_active && p.monthly_price > 0) setActiveCycle('monthly');
        else if (p.is_three_month_active && p.three_month_price > 0) setActiveCycle('quarterly');
        else if (p.is_six_month_active && p.six_month_price > 0) setActiveCycle('half_yearly');
        else if (p.is_yearly_active && p.yearly_price > 0) setActiveCycle('yearly');
        else if (p.is_weekly_active && p.weekly_price > 0) setActiveCycle('weekly');
      }
    } catch (err) {
      console.error('Failed to fetch pricing:', err);
    } finally {
      setLoadingPlans(false);
    }
  };



  /** Derive which billing cycles are available for any plan in current currency set */
  const getAvailableCycles = () => {
    const cycleSet = { monthly: false, quarterly: false, half_yearly: false, yearly: false, weekly: false };
    plans.forEach(p => {
      if (p.is_monthly_active && p.monthly_price > 0) cycleSet.monthly = true;
      if (p.is_three_month_active && p.three_month_price > 0) cycleSet.quarterly = true;
      if (p.is_six_month_active && p.six_month_price > 0) cycleSet.half_yearly = true;
      if (p.is_yearly_active && p.yearly_price > 0) cycleSet.yearly = true;
      if (p.is_weekly_active && p.weekly_price > 0) cycleSet.weekly = true;
    });
    return cycleSet;
  };

  const getPriceForCycle = (plan, cycle) => {
    if (cycle === 'weekly') return plan.weekly_price || 0;
    if (cycle === 'quarterly') return plan.three_month_price || 0;
    if (cycle === 'half_yearly') return plan.six_month_price || 0;
    if (cycle === 'yearly') return plan.yearly_price || 0;
    return plan.monthly_price || 0;
  };

  const sortedPlans = useMemo(() => {
    return [...plans]
      .filter(plan => {
        if (plan.is_coming_soon || plan.is_free) return true;
        if (activeCycle === 'weekly') return plan.is_weekly_active && plan.weekly_price > 0;
        if (activeCycle === 'quarterly') return plan.is_three_month_active && plan.three_month_price > 0;
        if (activeCycle === 'half_yearly') return plan.is_six_month_active && plan.six_month_price > 0;
        if (activeCycle === 'yearly') return plan.is_yearly_active && plan.yearly_price > 0;
        return plan.is_monthly_active && plan.monthly_price > 0;
      })
      .sort((a, b) => {
        if (a.is_coming_soon && !b.is_coming_soon) return 1;
        if (!a.is_coming_soon && b.is_coming_soon) return -1;
        if (a.is_coming_soon && b.is_coming_soon) return a.id - b.id; // sort by creation order
        if (a.is_free && !b.is_free) return -1; // Free plans always first
        if (!a.is_free && b.is_free) return 1;
        const priceA = getPriceForCycle(a, activeCycle);
        const priceB = getPriceForCycle(b, activeCycle);
        return priceA - priceB;
      });
  }, [plans, activeCycle, appliedCoupon]);

  const handleValidateCoupon = async (codeToValidate = null) => {
    const code = (typeof codeToValidate === 'string' ? codeToValidate : couponCode).trim().toUpperCase();
    if (!code) return;
    setIsValidatingCoupon(true);
    setCouponError('');
    try {
      const response = await api.post('superadmin/coupons/validate/', { code });
      if (response.data.valid) {
        setAppliedCoupon(response.data);
        setCouponCode(response.data.code);
        try {
          localStorage.setItem('active_coupon_code', response.data.code);
        } catch (_) { }
        emitAnalyticsEvent('coupon_applied', {
          coupon_code: response.data.code,
          percent_off: response.data.percent_off,
        });
        toast.success(`Coupon "${response.data.code}" applied! ${response.data.percent_off}% off.`);
      } else {
        setAppliedCoupon(null);
        setCouponError(response.data.error || 'Invalid coupon code');
        emitAnalyticsEvent('coupon_validation_failed', {
          coupon_code: code,
          reason: response.data.error || 'Invalid coupon code',
        });
      }
    } catch (err) {
      setAppliedCoupon(null);
      setCouponError(err.response?.data?.error || 'Failed to validate coupon');
      emitAnalyticsEvent('coupon_validation_failed', {
        coupon_code: code,
        reason: err.response?.data?.error || 'Failed to validate coupon',
      });
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  // Auto-apply stored ambassador/referral coupon code on open
  useEffect(() => {
    if (!isOpen) return;
    try {
      const storedCoupon = localStorage.getItem('active_coupon_code');
      if (storedCoupon && !appliedCoupon) {
        setCouponCode(storedCoupon);
        handleValidateCoupon(storedCoupon);
      }
    } catch (_) { }
  }, [isOpen]);

  const getSavingsBadge = (plan) => {
    if (activeCycle !== 'yearly' || !plan.monthly_price || plan.monthly_price <= 0) return null;
    const potentialAnnual = plan.monthly_price * 12;
    const actualAnnual = plan.yearly_price || 0;
    const diff = potentialAnnual - actualAnnual;
    if (diff <= 0) return null;
    return `Save ${formatPrice(diff, plan.currency || planCurrency)}`;
  };

  const [limits, setLimits] = useState({ chat: 2, quests: 1, curiosity: 2 });
  const [freeLimits, setFreeLimits] = useState({
    chat: 4, quests: 4, curiosity: 10, upload: 2, throttle_rate: '100/day'
  });
  const [defaultTrialDays, setDefaultTrialDays] = useState(null);

  useEffect(() => {
    const fetchLimits = async () => {
      try {
        const response = await axios.get(apiUrl('/health/'));
        if (response.data?.guest_limits) setLimits(response.data.guest_limits);
        if (response.data?.free_limits) setFreeLimits(response.data.free_limits);
        if (response.data) {
          setFreePlanMetadata({
            name: response.data.free_plan_name || 'Free',
            description: response.data.free_plan_description || 'Basic features for learners.'
          });
        }
        if (typeof response.data?.default_trial_days !== 'undefined') {
          setDefaultTrialDays(response.data.default_trial_days);
        }
      } catch (error) { /* keep defaults */ }
    };
    if (isOpen) fetchLimits();
  }, [isOpen]);

  const isPremiumView = useMemo(() => {
    if (mode === 'upgrade') return true;
    const isSigned = (
      isFreeUser ||
      (localStorage.getItem('student_token') ||
        localStorage.getItem('teacher_token') ||
        localStorage.getItem('access_token')) &&
      localStorage.getItem('is_guest_session') !== 'true'
    );
    // Use Number() to be safe with string error types
    const numericError = Number(errorType);
    return isSigned && (numericError === 402 || numericError === 429);
  }, [mode, isFreeUser, errorType]);

  const isVoiceTutor = errorType === 'voice_tutor' || feature === 'voice_tutor' || feature === 'teach_me';

  if (!isOpen) return null;

  const getRedirectTarget = () => {
    try {
      const currentUrl = `${location.pathname}${location.search}${location.hash}`;
      if (
        currentUrl &&
        currentUrl !== '/' &&
        !currentUrl.startsWith('/login') &&
        !currentUrl.startsWith('/signup') &&
        !currentUrl.includes('/login?') &&
        !currentUrl.includes('/signup?')
      ) {
        return currentUrl;
      }
    } catch (_) { }
    return null;
  };

  const handleLogin = () => {
    emitAnalyticsEvent('paywall_cta_clicked', { cta: 'login' });
    const target = getRedirectTarget();
    if (target) {
      try {
        sessionStorage.setItem('post_auth_redirect', target);
      } catch (_) { }
      navigate(`/login?redirect=${encodeURIComponent(target)}`);
    } else {
      navigate('/login');
    }
  };
  const handleSignup = () => {
    emitAnalyticsEvent('paywall_cta_clicked', { cta: 'signup' });
    const target = getRedirectTarget();
    if (target) {
      try {
        sessionStorage.setItem('post_auth_redirect', target);
      } catch (_) { }
      navigate(`/signup?redirect=${encodeURIComponent(target)}`);
    } else {
      navigate('/signup');
    }
  };

  const handleSubscribe = async (planId, billingCycle) => {
    // Check if user has an existing active or scheduled-to-cancel paid subscription
    // CANCELLED status in our DB means "Active until end of period"
    const hasPaidSub = userSubscription?.plan_id && (userSubscription?.status === 'ACTIVE' || userSubscription?.status === 'CANCELLED');

    // Only show confirmation if they are moving to a different plan
    const isChangingPlan = userSubscription?.plan_id !== planId;

    // Don't show upgrade prompt for free plans unless it's a specific requirement
    const toPlan = plans.find(p => p.id === planId);
    const isToFreePlan = toPlan?.is_free;
    emitAnalyticsEvent('plan_selected', {
      plan_id: planId,
      plan_name: toPlan?.name || null,
      billing_cycle: billingCycle,
      coupon_code: appliedCoupon?.code || null,
      has_paid_subscription: hasPaidSub,
      is_upgrade: Boolean(hasPaidSub && isChangingPlan && !isToFreePlan),
    });

    if (hasPaidSub && isChangingPlan && !isToFreePlan) {
      // Show upgrade/change confirmation dialog
      setUpgradingPlanId(planId);
      setPendingPlan({ id: planId, billing_cycle: billingCycle, name: toPlan?.name });
      setShowConfirmSub(true);
    } else if (isToFreePlan) {
      // Proceed to free plan directly
      setCheckoutLoading(true);
      try {
        await initiateCheckout(planId, billingCycle, portal, setCheckoutLoading, appliedCoupon?.code, navigate);
      } catch (error) {
        setCheckoutLoading(false);
      }
    } else {
      // For all paid plans, check if we already have billing info
      setPendingPlan({ id: planId, billing_cycle: billingCycle, name: toPlan?.name });

      try {
        const token = getAuthToken(portal);
        const res = await axios.get(apiUrl('users/profile/'), { headers: { Authorization: `Bearer ${token}` } });
        const p = res.data;

        // If billing info is already present, skip the modal
        if (p.billing_name && p.billing_state && p.billing_country) {
          proceedToCheckout();
        } else {
          openBillingModal('missing_profile_billing', {
            plan_id: planId,
            plan_name: toPlan?.name || null,
            billing_cycle: billingCycle,
          });
        }
      } catch (err) {
        console.warn('Failed to check profile for billing info, showing modal anyway', err);
        openBillingModal('profile_lookup_failed', {
          plan_id: planId,
          plan_name: toPlan?.name || null,
          billing_cycle: billingCycle,
        });
      }
    }
  };

  const confirmSubscription = async () => {
    if (!pendingPlan) return;
    setShowConfirmSub(false);

    // Also check for billing info here
    try {
      const token = getAuthToken(portal);
      const res = await axios.get(apiUrl('users/profile/'), { headers: { Authorization: `Bearer ${token}` } });
      const p = res.data;

      if (p.billing_name && p.billing_state && p.billing_country) {
        proceedToCheckout();
      } else {
        openBillingModal('confirm_subscription_missing_billing', {
          plan_id: pendingPlan.id,
          plan_name: pendingPlan.name || null,
          billing_cycle: pendingPlan.billing_cycle,
        });
      }
    } catch (err) {
      openBillingModal('confirm_subscription_profile_lookup_failed', {
        plan_id: pendingPlan.id,
        plan_name: pendingPlan.name || null,
        billing_cycle: pendingPlan.billing_cycle,
      });
    }
  };

  const proceedToCheckout = async (billingInfo = null) => {
    if (!pendingPlan) return;
    setShowBillingModal(false);
    setCheckoutLoading(true);
    emitAnalyticsEvent('paywall_cta_clicked', {
      cta: 'checkout',
      plan_id: pendingPlan.id,
      plan_name: pendingPlan.name || null,
      billing_cycle: pendingPlan.billing_cycle,
      coupon_code: appliedCoupon?.code || null,
      billing_details_supplied: Boolean(billingInfo),
    });
    try {
      // If billingInfo was passed (from modal), it's already saved by the modal itself.
      // If not passed, we are skipping the modal because it's already in the profile.

      // Initiate checkout with billing info (handled by backend session creation)
      await initiateCheckout(pendingPlan.id, pendingPlan.billing_cycle, portal, setCheckoutLoading, appliedCoupon?.code, navigate);
    } catch (error) {
      console.error('[AuthPromptDialog] Checkout failed:', error);
      toast.error(error.response?.data?.error || 'Failed to initiate checkout');
      setCheckoutLoading(false);
    }
  };

  const handleSuccessContinue = () => {
    setShowSuccessModal(false);
    setPendingPlan(null);
    onClose();
    window.location.reload();
  };

  const getFeatureRoute = () => {
    if (isTeacher) return '/teacher/teach';
    const featureLower = feature?.toLowerCase() || '';
    if (featureLower.includes('curiosity')) return '/student/curiosity';
    if (featureLower.includes('chat')) return '/student/chat';
    if (featureLower.includes('quest')) return '/student/quests';
    if (featureLower.includes('budlee')) return '/student/learn';
    if (featureLower.includes('learn')) return '/student/learn';
    return '/student/learn';
  };

  const getFeatureDetails = () => {
    const features = {
      'budlee_content': { icon: Sparkles, title: 'Budlee Learn', limit: '1 Learning Unit per day' },
      'budlee content': { icon: Sparkles, title: 'Budlee Learn', limit: '1 Learning Unit per day' },
      'chat': { icon: MessageSquare, title: 'Chat', limit: `${limits.chat} conversation${limits.chat !== 1 ? 's' : ''}` },
      'quests': { icon: Target, title: 'Quests', limit: `${limits.quests} quest per session` },
      'quest': { icon: Target, title: 'Quests', limit: `${limits.quests} quest per session` },
      'curiosity centre': { icon: Sparkles, title: 'Curiosity Centre', limit: `${limits.curiosity} content piece${limits.curiosity !== 1 ? 's' : ''}` },
      'curiosity': { icon: Sparkles, title: 'Curiosity Centre', limit: `${limits.curiosity} content piece${limits.curiosity !== 1 ? 's' : ''}` },
      'guest limit': { icon: Lock, title: 'API Request', limit: 'Rate limit exceeded' },
    };
    return features[feature?.toLowerCase()] || {
      icon: Lock,
      title: feature,
      limit: effectiveIsFreeUser ? 'Free limit reached' : 'Guest limit reached'
    };
  };

  const featureDetails = getFeatureDetails();
  const FeatureIcon = featureDetails.icon;

  const accountBenefits = isVoiceTutor ? [
    { icon: Phone, text: 'Real-time voice conversations with AI Tutors' },
    { icon: GraduationCap, text: 'Interactive "Teach Me" breakdown for any card' },
    { icon: CheckCircle, text: 'Save your study progress, rewards, and notes' },
  ] : effectiveIsFreeUser ? (
    isTeacher ? [
      { icon: CheckCircle, text: 'Unlimited access to all teaching tools' },
      { icon: Zap, text: 'Priority support and dedicated assistance' },
      { icon: CheckCircle, text: 'Advanced AI models and classroom features' },
    ] : [
      { icon: CheckCircle, text: 'Unlimited access to all features' },
      { icon: Zap, text: 'Priority support and faster responses' },
      { icon: CheckCircle, text: 'Advanced AI models and exclusive content' },
    ]
  ) : (
    isTeacher ? [
      { icon: CheckCircle, text: 'Save your curriculum and lesson plans' },
      { icon: Zap, text: 'Personalized teaching recommendations' },
      { icon: CheckCircle, text: 'Access your saved teaching materials and notes' },
    ] : [
      { icon: CheckCircle, text: 'Save your progress and learning history' },
      { icon: Zap, text: 'Personalized learning recommendations' },
      { icon: CheckCircle, text: 'Access your saved quests and notes' },
    ]
  );

  const availableCycles = getAvailableCycles();
  const cycleLabels = {
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    half_yearly: 'Half-Yearly',
    yearly: 'Yearly',
    weekly: 'Weekly'
  };
  const orderedKeys = ['weekly', 'monthly', 'quarterly', 'half_yearly', 'yearly'];
  const visibleCycles = orderedKeys.filter(key => availableCycles[key]);

  // Derive current subscription's cost for upgrade comparison
  const currentPlanRef = plans.find(p => p.name === userSubscription?.plan_name);
  const currentActualCycle = userSubscription?.billing_cycle?.toLowerCase?.();
  const currentPayingPrice = currentPlanRef ? (
    currentActualCycle === 'weekly' ? parseFloat(currentPlanRef.weekly_price || 0) :
      (currentActualCycle === 'quarterly' || currentActualCycle === 'three_months') ? parseFloat(currentPlanRef.three_month_price || 0) :
        (currentActualCycle === 'half_yearly' || currentActualCycle === 'six_months') ? parseFloat(currentPlanRef.six_month_price || 0) :
          currentActualCycle === 'yearly' ? parseFloat(currentPlanRef.yearly_price || 0) :
            parseFloat(currentPlanRef.monthly_price || 0)
  ) : 0;

  return (
    <div
      className={cn(
        "fixed inset-0 z-[1500] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 transition-all duration-300",
      )}
      onClick={(e) => {
        if (e.target === e.currentTarget && Number(errorType) !== 429 && errorType !== 'content_mismatch') {
          closePrompt('backdrop');
        }
      }}
    >
      {/* Checkout Loading Overlay */}
      {checkoutLoading && (
        <div className="absolute inset-0 z-[1000] flex flex-col items-center justify-center bg-gray-900/90 backdrop-blur-xl transition-all duration-300">
          <div className="relative">
            <div className="absolute inset-0 bg-blue-500/20 blur-3xl rounded-full scale-150 animate-pulse"></div>
            <div className="relative flex flex-col items-center gap-6">
              <Loader2 className="w-16 h-16 text-blue-500 animate-spin" />
              <div className="text-center space-y-2">
                <h3 className="text-xl font-bold text-white tracking-tight">Preparing Secure Checkout</h3>
                <p className="text-gray-400 text-sm max-w-[250px] leading-relaxed">
                  Please wait while we set up your subscription. This may take a few seconds...
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={cn(
        "bg-[hsl(var(--card-bg))] shadow-2xl border border-[hsl(var(--card-border))] text-foreground flex flex-col overflow-y-auto overflow-x-hidden transition-all duration-500 rounded-2xl",
        "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-1.5rem)] h-auto max-h-[calc(100dvh-(env(safe-area-inset-top)+env(safe-area-inset-bottom)+9rem))]",
        "md:max-h-[85vh]",
        isPremiumView ? "md:max-w-6xl" : "md:max-w-[480px]"
      )}>

        {/* Global Close Button */}
        {showCloseButton && Number(errorType) !== 429 && (
          <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-[60]">
            <button
              onClick={() => closePrompt('close_button')}
              className="p-1.5 sm:p-2 rounded-full bg-black/20 hover:bg-black/40 text-white transition-all backdrop-blur-sm"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        )}

        {loadingPlans ? (
          <div className="flex-1 flex flex-col items-center justify-center p-10 space-y-6 animate-in fade-in duration-700 min-h-[300px] md:min-h-[500px]">
            <div className="relative">
              <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full scale-150 animate-pulse"></div>
              <Loader2 className="w-16 h-16 text-primary animate-spin relative z-10" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-xl font-bold text-foreground tracking-tight">
                {isPremiumView ? "Fetching Plans" : "Getting Ready"}
              </h3>
              <p className="text-muted-foreground text-sm max-w-[280px] mx-auto leading-relaxed">
                {isPremiumView
                  ? (isTeacher
                    ? "Preparing best plans for your teaching experience..."
                    : "Preparing best plans for your personalized learning experience...")
                  : (isTeacher
                    ? "Preparing your teaching workspace..."
                    : "Preparing your personalized learning space...")}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Hero Section */}
            {!isPremiumView && (
              <div className={cn(
                "relative border-b border-border p-6 flex-shrink-0",
                isVoiceTutor
                  ? "bg-gradient-to-r from-emerald-600/20 via-teal-600/20 to-cyan-600/10"
                  : "bg-gradient-to-r from-red-600/20 via-orange-600/20 to-red-600/10"
              )}>
                <div className="flex flex-col items-center justify-center gap-3 text-center">
                  <div className={cn(
                    "p-2.5 rounded-xl shadow-md",
                    isVoiceTutor
                      ? "bg-gradient-to-br from-teal-500 to-emerald-600 text-white"
                      : "bg-gradient-to-br from-orange-500 to-red-500 text-white"
                  )}>
                    {isVoiceTutor ? (
                      <GraduationCap className="w-6 h-6 text-white" />
                    ) : (
                      <Lock className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <h2 className="text-2xl font-bold text-foreground">
                    {isVoiceTutor ? 'Unlock AI Voice Tutor'
                      : errorType === 'guest_share' ? 'Login to Save Progress'
                        : errorType === 'content_mismatch' ? 'Content Unavailable'
                          : 'Start Your Journey'}
                  </h2>
                  <p className="text-muted-foreground text-sm mt-0.5 max-w-sm">
                    {isVoiceTutor
                      ? "Speaking with your AI Tutor and using interactive 'Teach Me' sessions requires a free account. Sign up in seconds to start voice conversations!"
                      : errorType === 'guest_share' ? 'To save this content to your account kindly sign up or login.'
                        : errorType === 'content_mismatch' ? 'This content is not for the curriculum and grade you have enrolled.'
                          : Number(errorType) === 429 ? 'You have used your guest usage limit. Please create an account to continue learning.'
                            : 'Create an account to track your progress and unlock more features.'}
                  </p>
                </div>
              </div>
            )}

            {/* Content - Scrollable */}
            <div className={cn("overflow-y-auto flex-1", isPremiumView ? "p-4 sm:p-6 md:px-10" : "p-6 sm:p-10")}>
              {isPremiumView ? (
                (!show_subscriptions && Capacitor.isNativePlatform()) ? (
                  /* Native View - Redirect to Website */
                  <div className="flex flex-col items-center justify-center py-8 sm:py-12 text-center space-y-6 sm:space-y-8 animate-in fade-in zoom-in duration-500">
                    <div className="relative">
                      <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full scale-150 animate-pulse"></div>
                      <div className="relative bg-gradient-to-br from-primary to-blue-600 p-4 sm:p-6 rounded-3xl shadow-2xl">
                        <Sparkles className="w-8 h-8 sm:w-12 sm:h-12 text-white" />
                      </div>
                    </div>

                    <div className="space-y-2 sm:space-y-3 max-w-md">
                      <h2 className="text-xl sm:text-3xl font-bold tracking-tight text-foreground">
                        {isTeacher ? "Unlock Unlimited Teaching Tools" : "Unlock Unlimited Learning"}
                      </h2>
                      <p className="text-xs sm:text-base text-muted-foreground leading-relaxed">
                        To comply with platform policies, subscription management and upgrades must be completed on our website.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 w-full max-w-lg">
                      {accountBenefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-center gap-2.5 sm:gap-3 p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-muted/50 border border-border/50">
                          <benefit.icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0" />
                          <span className="text-xs sm:text-sm font-semibold text-foreground/90">{benefit.text}</span>
                        </div>
                      ))}
                    </div>

                    <div className="pt-2 sm:pt-4 w-full max-w-md">
                      <Button
                        size="lg"
                        className="w-full h-11 sm:h-14 rounded-xl sm:rounded-2xl text-sm sm:text-lg font-bold bg-primary hover:bg-primary/90 text-primary-foreground shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98] group"
                        onClick={() => {
                          const email = getUserData(portal)?.username || '';
                          const billingUrl = `${WEB_URL}/login?redirect=/${isTeacher ? 'teacher' : 'student'}/profile&email=${encodeURIComponent(email)}`;
                          Browser.open({ url: billingUrl });
                        }}
                      >
                        Manage Account
                        <ExternalLink className="ml-2 w-4 h-4 sm:w-5 sm:h-5 opacity-70 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                      </Button>
                      <p className="text-[9px] sm:text-[10px] text-muted-foreground mt-3 sm:mt-4 uppercase tracking-[0.2em] font-black opacity-50">
                        Secure SSL Protection • Fast Sync
                      </p>
                    </div>
                    {planCurrency === 'INR' && (
                      <p className="text-[9px] sm:text-[10px] text-muted-foreground text-center mt-4 sm:mt-6">
                        * Applicable taxes will be added to the above plan amount.
                      </p>
                    )}
                  </div>
                ) : (
                  /* Standard Web - 5-Card Subscription Layout */
                  <div className="max-w-[1600px] mx-auto space-y-4 sm:space-y-6">
                    <div className="text-center space-y-1 sm:space-y-2">
                      <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                        {isTeacher ? "Choose Your Subscription Plan" : "Choose Your Learning Plan"}
                      </h2>
                    </div>

                    {/* Cycle Toggle - Only show when multiple cycles are available */}
                    {visibleCycles.length > 1 && (
                      <div className="flex justify-center">
                        <div className="bg-muted/50 p-1 rounded-full flex items-center relative gap-1 border border-border">
                          {visibleCycles.map((cycle) => (
                            <div key={cycle} className="relative">
                              <button
                                onClick={() => setActiveCycle(cycle)}
                                className={cn(
                                  "rounded-full px-4 sm:px-8 py-1.5 sm:py-2 text-[11px] sm:text-xs font-bold transition-all duration-300 relative z-10",
                                  activeCycle === cycle ? "bg-primary text-primary-foreground shadow-lg" : "text-muted-foreground hover:text-foreground"
                                )}
                              >
                                {cycleLabels[cycle] || (cycle.charAt(0).toUpperCase() + cycle.slice(1))}
                              </button>
                              {cycle === 'yearly' && (
                                <div className="absolute -top-2.5 -right-1 z-20">
                                  <Badge className="bg-[#E88B1E] hover:bg-[#E88B1E] text-white text-[7px] sm:text-[8px] py-0 px-1 sm:px-1.5 rounded-full border-none font-black uppercase tracking-wider">
                                    BEST
                                  </Badge>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Coupon Code Section - Shown for available payment gateways (including Mock) */}
                    {(stripeEnabled || rpayEnabled || mockEnabled) && (
                      <div className="flex flex-col items-center space-y-1.5 sm:space-y-2 mx-auto">
                        <div className="text-[9px] sm:text-[10px] text-muted-foreground font-bold uppercase tracking-widest opacity-80 flex items-center justify-center text-center leading-relaxed">
                          {appliedCoupon ? (
                            <span className="text-primary">✓ Coupon Applied: {appliedCoupon.percent_off}% off</span>
                          ) : couponError ? (
                            <span className="text-destructive whitespace-normal block px-2 max-w-[280px]">
                              {couponError}
                            </span>
                          ) : (
                            <span className="opacity-60">Coupon Code</span>
                          )}
                        </div>
                        <div className="flex gap-1.5 sm:gap-2 justify-center">
                          <input
                            type="text"
                            placeholder="ENTER CODE"
                            value={couponCode}
                            onChange={(e) => {
                              setCouponCode(e.target.value);
                              if (couponError) setCouponError('');
                            }}
                            className={cn(
                              "w-[150px] sm:w-[180px] bg-muted/50 border border-border rounded-xl h-9 sm:h-10 px-3 sm:px-4 text-center uppercase tracking-widest text-[11px] sm:text-xs font-bold transition-all focus:border-primary/50 outline-none text-foreground",
                              appliedCoupon && "border-primary/50 text-primary",
                              couponError && "border-destructive/50"
                            )}
                            disabled={!!appliedCoupon}
                          />
                          {appliedCoupon ? (
                            <button
                              onClick={() => {
                                setAppliedCoupon(null);
                                setCouponCode('');
                              }}
                              className="bg-destructive/10 hover:bg-destructive/20 text-destructive border border-destructive/20 rounded-xl h-9 sm:h-10 px-3 sm:px-4 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider transition-all"
                            >
                              Remove
                            </button>
                          ) : (
                            <button
                              onClick={handleValidateCoupon}
                              disabled={isValidatingCoupon || !couponCode.trim()}
                              className="bg-muted/30 hover:bg-muted/50 text-foreground border border-border rounded-xl h-9 sm:h-10 px-3 sm:px-4 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider transition-all disabled:opacity-50"
                            >
                              {isValidatingCoupon ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply'}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Ask Parent Unlock Banner */}
                    {(() => {
                      const isPaidUser = userSubscription?.status === 'ACTIVE' && userSubscription?.plan_name && userSubscription?.plan_name !== 'FREE';
                      return !isTeacher && (mode === 'upgrade' || isFreeUser) && !hasParentWhatsapp && !isPaidUser ? (
                        <div className="mb-3 sm:mb-4 p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex flex-col sm:flex-row items-center justify-between gap-2.5 sm:gap-3 text-center sm:text-left">
                          <div>
                            <h4 className="font-bold text-xs sm:text-sm text-foreground">Unlock 24 Hours of Unlimited Access!</h4>
                            <p className="text-[11px] sm:text-xs text-muted-foreground">Send your analytics to your parent over WhatsApp and ask them to unlock it for you.</p>
                          </div>
                          <button
                            onClick={() => {
                              closePrompt('parent_whatsapp_unlock');
                              window.dispatchEvent(new CustomEvent('paymentRequired', { detail: { feature, statusCode: 402, directParentUnlock: true } }));
                            }}
                            className="py-1.5 sm:py-2 px-3 sm:px-4 rounded-xl bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-bold text-[11px] sm:text-xs shadow-md shadow-emerald-600/20 transition-all whitespace-nowrap border-0"
                          >
                            Ask your parent
                          </button>
                        </div>
                      ) : null;
                    })()}

                    {/* Plan Grid */}
                    <div className="flex flex-wrap justify-center gap-3 sm:gap-4 items-stretch pb-6 sm:pb-8 pt-2 sm:pt-4">
                      {(() => {
                        const anyPlanHasPromo = sortedPlans.some(p => p.is_promo);
                        return sortedPlans.map((plan, planIndex) => {
                          const price = getPriceForCycle(plan, activeCycle);
                          const originalPrice = activeCycle === 'yearly'
                            ? plan.original_price_yearly
                            : activeCycle === 'half_yearly'
                              ? plan.original_price_six_month
                              : activeCycle === 'quarterly'
                                ? plan.original_price_three_month
                                : activeCycle === 'weekly'
                                  ? plan.original_price_weekly
                                  : plan.original_price_monthly;

                          const isPopular = plan.is_most_popular;
                          // Handle "current plan" check: match either by plan ID or by status if it's the free plan
                          const isSamePlan = userSubscription?.plan_id === plan.id ||
                            (plan.is_free && (!userSubscription || userSubscription.status === 'FREE' || userSubscription.status === 'ACTIVE' && !userSubscription.plan_id));

                          // Check if this is an upgrade or downgrade when in upgrade mode
                          const isUpgradeMode = mode === 'upgrade' && userSubscription?.plan_id;
                          let isDowngrade = false;
                          let isDisabledForDowngrade = false;

                          if (isUpgradeMode) {
                            // Find current plan in sorted list
                            const currentPlanIndex = sortedPlans.findIndex(p =>
                              userSubscription?.plan_id === p.id ||
                              (p.is_free && (!userSubscription || userSubscription.status === 'FREE' || (userSubscription.status === 'ACTIVE' && !userSubscription.plan_id)))
                            );

                            // If current plan is at or after this plan in the list, it's a downgrade or same
                            if (currentPlanIndex >= 0 && planIndex <= currentPlanIndex) {
                              isDowngrade = true;
                              isDisabledForDowngrade = true;
                            }
                          }

                          return (
                            <div key={plan.id} className="flex flex-col w-full sm:w-[250px] self-stretch">
                              <PlanCard
                                name={plan.name}
                                price={price}
                                originalPrice={originalPrice}
                                currency={plan.currency || planCurrency}
                                cycle={activeCycle}
                                isCurrent={isSamePlan}
                                isPopular={isPopular}
                                loading={checkoutLoading}
                                onSubscribe={() => handleSubscribe(plan.id, activeCycle)}
                                appliedCoupon={appliedCoupon}
                                planData={plan}
                                isFree={plan.is_free}
                                freeLimits={freeLimits}
                                isComingSoon={plan.is_coming_soon}
                                buildPlanBenefits={buildPlanBenefits}
                                freePlanMetadata={freePlanMetadata}
                                isDowngrade={isDowngrade}
                                isDisabledForDowngrade={isDisabledForDowngrade}
                                isPromo={plan.is_promo}
                                anyPlanHasPromo={anyPlanHasPromo}
                              />
                            </div>
                          );
                        });
                      })()}

                      {/* Fallback if no plans available */}
                      {sortedPlans.length === 0 && (
                        <div className="flex flex-col w-full sm:w-[250px] self-stretch">
                          <PlanCard
                            name={freePlanMetadata.name}
                            price={0}
                            currency={planCurrency}
                            cycle={activeCycle}
                            isCurrent={true}
                            onSubscribe={() => { }}
                            isFree={true}
                            freeLimits={freeLimits}
                            buildPlanBenefits={buildPlanBenefits}
                            freePlanMetadata={freePlanMetadata}
                          />
                        </div>
                      )}
                    </div>
                    {planCurrency === 'INR' && (
                      <p className="text-[9px] sm:text-[10px] text-muted-foreground text-center mt-4 sm:mt-6">
                        * Applicable taxes will be added to the above plan amount.
                      </p>
                    )}
                  </div>
                )) : (
                /* Standard Guest / Sign-up View */
                <div className="space-y-4">
                  {/* Account Benefits */}
                  {errorType !== 'content_mismatch' && (
                    <div className="max-w-md mx-auto text-center">
                      <h3 className="text-foreground font-semibold mb-3 flex items-center justify-center gap-1 text-sm">
                        <Unlock className="w-4 h-4 text-emerald-500" />
                        With an Account
                      </h3>
                      <div className="space-y-2">
                        {accountBenefits.map((item, idx) => {
                          const Icon = item.icon;
                          return (
                            <div key={idx} className="flex items-center justify-center gap-2 text-muted-foreground">
                              <Icon className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                              <span className="text-xs font-medium text-foreground/80">{item.text}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* CTA Section */}
                  <div className="bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded-2xl p-4 sm:p-6">
                    {errorType === 'content_mismatch' ? (
                      <div className="flex flex-col gap-3 max-w-[320px] mx-auto w-full">
                        <button
                          onClick={() => {
                            onClose();
                            navigate(`/${portal}/learn`);
                          }}
                          className="w-full py-2 px-4 text-sm rounded-lg bg-[hsl(var(--card-bg))] hover:bg-[hsl(var(--sidebar-hover))] text-foreground font-semibold transition-all duration-200 border border-[hsl(var(--card-border))]"
                        >
                          Go to Homepage
                        </button>
                      </div>
                    ) : (
                      <>
                        <p className="text-muted-foreground text-center mb-4 font-medium text-xs">
                          {isVoiceTutor
                            ? "Create an account to start speaking with your AI Tutor"
                            : isTeacher
                              ? "Create an account to unlock unlimited teaching potential"
                              : "Create an account to unlock unlimited learning potential"}
                        </p>

                        <div className="space-y-3 max-w-[320px] mx-auto w-full">
                          <div className="grid gap-2">
                            <GoogleAuthButton
                              onSuccess={() => {
                                onClose();
                                const target = getRedirectTarget();
                                if (target) {
                                  try { sessionStorage.setItem('post_auth_redirect', target); } catch (_) { }
                                  navigate(`/signup?redirect=${encodeURIComponent(target)}`);
                                } else {
                                  navigate('/signup');
                                }
                              }}
                              text="signup_with"
                            />
                            <AppleAuthButton
                              onSuccess={() => {
                                onClose();
                                const target = getRedirectTarget();
                                if (target) {
                                  try { sessionStorage.setItem('post_auth_redirect', target); } catch (_) { }
                                  navigate(`/signup?redirect=${encodeURIComponent(target)}`);
                                } else {
                                  navigate('/signup');
                                }
                              }}
                              text="signup_with"
                            />
                          </div>

                          <Button
                            variant="outline"
                            className="w-full h-10 text-sm font-semibold rounded-xl border-[hsl(var(--card-border))] bg-[hsl(var(--card-bg))] hover:bg-[hsl(var(--sidebar-hover))] text-foreground transition-all"
                            onClick={handleSignup}
                          >
                            Sign up with Email
                          </Button>

                          <div className="pt-2 text-center">
                            <button
                              onClick={handleLogin}
                              className="text-xs text-muted-foreground hover:text-[hsl(var(--green-bright))] dark:hover:text-emerald-400 transition-colors font-medium"
                            >
                              Already have an account? <span className="underline underline-offset-2">Login</span>
                            </button>
                          </div>
                        </div>

                        {(errorType === 402 || isVoiceTutor) && (
                          <div className="mt-4 text-center">
                            <button
                              onClick={onClose}
                              className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground/60 hover:text-foreground transition-colors"
                            >
                              Continue exploring as guest
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Footer Note */}
              {errorType !== 'content_mismatch' && (
                <div className="text-[11px] text-muted-foreground text-center mt-6 opacity-80 flex flex-col items-center gap-1">
                  {!effectiveIsFreeUser && (
                    <p>By signing up, you agree to our</p>
                  )}
                  <div className="flex items-center gap-2 font-medium">
                    <a
                      href="https://budlee.ai/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-primary transition-colors underline underline-offset-2"
                    >
                      Terms of Service
                    </a>
                    <span className="opacity-40 select-none">•</span>
                    <a
                      href="https://budlee.ai/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-primary transition-colors underline underline-offset-2"
                    >
                      Privacy Policy
                    </a>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ConfirmationModal
        isOpen={showConfirmSub}
        onOpenChange={setShowConfirmSub}
        title={upgradingPlanId ? "Confirm Plan Change" : "Confirm Subscription"}
        description={upgradingPlanId ?
          `You are currently on the ${userSubscription?.plan_name} plan. Confirming this will switch you to the ${pendingPlan?.name} plan. Your new plan benefits will activate once the payment is successful, and your old plan will be automatically cancelled.`
          : `Are you sure you want to subscribe to the ${pendingPlan?.name} (${pendingPlan?.billing_cycle}) plan?`
        }
        confirmText={checkoutLoading ? "Processing..." : (upgradingPlanId ? "Confirm & Upgrade" : "Confirm & Subscribe")}
        cancelText="Cancel"
        onConfirm={confirmSubscription}
        onCancel={() => {
          setShowConfirmSub(false);
          setPendingPlan(null);
          setUpgradingPlanId(null);
        }}
        zIndex="z-[2010]"
      />

      <BillingDetailsModal
        isOpen={showBillingModal}
        onClose={() => setShowBillingModal(false)}
        onConfirm={proceedToCheckout}
        portal={portal}
      />

      <ConfirmationModal
        isOpen={showSuccessModal}
        onOpenChange={setShowSuccessModal}
        title="Subscription Activated!"
        description={`Success! You are now subscribed to the ${pendingPlan?.name} plan. All limits have been removed, and you now have unlimited access to all features.`}
        confirmText="Continue"
        hideCancel={true}
        onConfirm={handleSuccessContinue}
        zIndex="z-[2020]"
      />
    </div >
  );
}

export function PlanCard({ name, price, currency, cycle, isCurrent, isPopular, onSubscribe, loading, isFree, freeLimits, appliedCoupon, planData, buildPlanBenefits, freePlanMetadata, originalPrice, isComingSoon, isDowngrade, isDisabledForDowngrade, isPromo, anyPlanHasPromo }) {
  const benefitData = useMemo(() => {
    if (isFree && freePlanMetadata) {
      return buildPlanBenefits(freePlanMetadata);
    }

    if (planData && buildPlanBenefits) {
      return buildPlanBenefits(planData);
    }

    return { preTitle: '', listHeading: '', points: [] };
  }, [isFree, freePlanMetadata, planData, buildPlanBenefits]);

  const cycleMultiplier = cycle === 'yearly' ? 12 : cycle === 'half_yearly' ? 6 : cycle === 'quarterly' ? 3 : 1;
  const cycleDays = cycle === 'yearly' ? 365 : cycle === 'half_yearly' ? 180 : cycle === 'quarterly' ? 90 : cycle === 'weekly' ? 7 : 30;
  const isMultiMonth = cycleMultiplier > 1;

  const discountedPrice = useMemo(() => {
    if (isFree || !appliedCoupon) return price;
    return (price * (1 - appliedCoupon.percent_off / 100)).toFixed(0);
  }, [price, appliedCoupon, isFree]);

  // Normalized monthly equivalent for headline display
  const monthlyEquivalent = isFree ? 0 : isMultiMonth ? (discountedPrice / cycleMultiplier) : discountedPrice;
  const originalMonthlyEquivalent = (originalPrice && isMultiMonth) ? (parseFloat(originalPrice) / cycleMultiplier) : originalPrice;
  const showOriginalPrice = !isComingSoon && originalPrice && parseFloat(originalPrice) > parseFloat(price);

  // Auto-calculated dynamic savings vs 1-month plan price (or original price discount)
  const monthlyBasePrice = parseFloat(planData?.monthly_price || 0);
  const savingsBadgeText = useMemo(() => {
    if (isFree || isComingSoon) return null;
    if (isMultiMonth && monthlyBasePrice > 0) {
      const eq = discountedPrice / cycleMultiplier;
      if (eq < monthlyBasePrice) {
        const pct = Math.round(((monthlyBasePrice - eq) / monthlyBasePrice) * 100);
        if (pct > 0) return `Save ${pct}% vs Monthly`;
      }
    }
    if (showOriginalPrice && parseFloat(originalPrice) > parseFloat(discountedPrice)) {
      const pct = Math.round(((parseFloat(originalPrice) - parseFloat(discountedPrice)) / parseFloat(originalPrice)) * 100);
      if (pct > 0) return `Save ${pct}%`;
    }
    return null;
  }, [isFree, isComingSoon, isMultiMonth, monthlyBasePrice, discountedPrice, cycleMultiplier, showOriginalPrice, originalPrice]);

  const billingCadenceText = isFree ? null : cycle === 'yearly'
    ? `Billed ${formatPrice(discountedPrice, currency)} annually`
    : cycle === 'half_yearly'
      ? `Billed ${formatPrice(discountedPrice, currency)} every 6 months`
      : cycle === 'quarterly'
        ? `Billed ${formatPrice(discountedPrice, currency)} every 3 months`
        : cycle === 'weekly'
          ? `Billed ${formatPrice(discountedPrice, currency)} weekly`
          : `Billed ${formatPrice(discountedPrice, currency)} monthly`;

  const monthlyTaxAmount = (discountedPrice * 0.18) / cycleMultiplier;

  const featureList = useMemo(() => {
    if (isComingSoon) return [];
    if (isFree) {
      return [
        'Budlee Learn: 1 Unit / day',
        `Chat: ${freeLimits?.chat || 5} Messages`,
        `Quests: ${freeLimits?.quests || 4} Quests`,
        `Curiosity: ${freeLimits?.curiosity || 5} Items`,
        `Uploads: ${freeLimits?.upload || 2} Files`
      ];
    }
    if (planData) {
      return [
        'Budlee Learn: Unlimited Access',
        `Chat: ${planData.chat || '∞'} Messages`,
        `Curiosity: ${planData.curiosity || '∞'} Items`,
        `Quests: ${planData.quest || '∞'} Quests`,
        `Uploads: ${planData.upload || '∞'} Files`
      ];
    }
    return [];
  }, [isComingSoon, isFree, freeLimits, planData]);

  return (
    <div className={cn(
      "relative rounded-2xl sm:rounded-[28px] p-4 sm:p-6 flex flex-col flex-1 h-full w-full transition-all duration-300",
      isComingSoon ? "border-2 border-blue-500/50 bg-[hsl(var(--main-bg))]" : isPopular ? "border-2 border-emerald-500 bg-[hsl(var(--card-bg))]" : "border-2 border-[hsl(var(--card-border))] bg-[hsl(var(--main-bg))]/50",
      isCurrent && "border-emerald-500/50"
    )}>
      {(isPopular || isComingSoon) && (
        <div className="absolute -top-2.5 sm:-top-3 left-1/2 -translate-x-1/2 z-10 w-full flex justify-center gap-2">
          {isPopular && (
            <Badge className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white text-[9px] sm:text-[10px] font-bold py-0.5 sm:py-1 px-2.5 sm:px-3 rounded-lg flex items-center gap-1 sm:gap-1.5 border-none uppercase tracking-wide shadow-md shadow-emerald-500/20">
              <Star className="w-2.5 h-2.5 sm:w-3 sm:h-3 fill-current" />
              MOST POPULAR
            </Badge>
          )}
          {isComingSoon && (
            <Badge className="bg-blue-600 hover:bg-blue-600 text-white text-[9px] sm:text-[10px] font-bold py-0.5 sm:py-1 px-2.5 sm:px-3 rounded-lg flex items-center gap-1 sm:gap-1.5 border-none uppercase tracking-wide shadow-lg">
              <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3 fill-current" />
              COMING SOON
            </Badge>
          )}
        </div>
      )}

      {/* Standardized Title & PreTitle slot - Fixed height */}
      <div className="text-center h-[52px] sm:h-[56px] flex flex-col justify-center mb-2">
        <h3 className="text-base sm:text-xl font-bold tracking-tight text-foreground mb-0.5 leading-tight line-clamp-1">
          {name}
        </h3>

        {benefitData.preTitle ? (
          <p className="text-[9px] sm:text-[10px] text-muted-foreground font-bold uppercase tracking-widest line-clamp-2 px-1">
            {benefitData.preTitle}
          </p>
        ) : (
          <div className="h-[12px]" />
        )}
      </div>

      {anyPlanHasPromo && (
        <div className="flex justify-center h-[24px] sm:h-[26px] items-center mb-1">
          {isPromo && !isComingSoon ? (
            <div className="bg-gradient-to-r from-orange-400 to-rose-500 text-white text-[9px] sm:text-[10px] font-black py-0.5 sm:py-1 px-2.5 sm:px-4 rounded-full shadow-[0_4px_12px_rgba(244,63,94,0.3)] border-2 border-white/10 animate-[bounce_2s_infinite] uppercase tracking-widest flex items-center gap-1 sm:gap-1.5 whitespace-nowrap">
              <Sparkles className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-yellow-200 fill-current" />
              <span>Promo Offer</span>
            </div>
          ) : (
            <div className="h-full w-full" />
          )}
        </div>
      )}

      {/* Standardized Pricing & Savings Badge slot - Compact fixed height so all buttons align horizontally */}
      <div className="h-[102px] sm:h-[108px] flex flex-col items-center justify-center mb-3 sm:mb-4">
        {!isComingSoon ? (
          <div className="flex flex-col items-center justify-center w-full">
            <div className="h-[14px] flex items-center justify-center mb-0.5">
              {originalMonthlyEquivalent && parseFloat(originalMonthlyEquivalent) > monthlyEquivalent ? (
                <span className="text-[10px] sm:text-xs font-semibold text-muted-foreground line-through leading-none">
                  {formatPrice(originalMonthlyEquivalent, currency)}
                </span>
              ) : null}
            </div>

            <div className="flex items-baseline justify-center gap-1 sm:gap-1.5 flex-nowrap whitespace-nowrap leading-none">
              <span className="text-2xl sm:text-3xl font-black text-foreground tracking-tight sm:tracking-tighter">
                {formatPrice(isFree ? 0 : monthlyEquivalent, currency)}
              </span>
              {!isFree && (
                <div className="inline-flex flex-col items-start justify-end ml-1 self-stretch pb-0.5">
                  {currency === 'INR' && monthlyTaxAmount > 0 && (
                    <span className="text-[8px] sm:text-[9px] font-semibold text-muted-foreground/80 leading-tight whitespace-nowrap">
                      (+{formatPrice(monthlyTaxAmount, currency, 0)} Taxes / mo)
                    </span>
                  )}
                  <span className="text-muted-foreground text-xs font-bold leading-none">
                    /{cycle === 'weekly' ? 'week' : 'mo'}
                  </span>
                </div>
              )}
            </div>

            <div className="h-[14px] flex items-center justify-center mt-0.5">
              {billingCadenceText ? (
                <span className="text-[10px] sm:text-[11px] text-muted-foreground font-medium leading-none">
                  {billingCadenceText}
                </span>
              ) : (
                <span className="text-[10px] sm:text-[11px] invisible select-none leading-none">
                  Billed monthly
                </span>
              )}
            </div>

            <div className="h-[13px] flex items-center justify-center mt-0.5">
              {!isFree ? (
                <span className="text-[9px] sm:text-[10px] font-black text-muted-foreground uppercase tracking-widest leading-none">
                  {(() => {
                    const daily = discountedPrice / cycleDays;
                    return `${formatPrice(daily, currency, 2)} per day`;
                  })()}
                </span>
              ) : (
                <span className="text-[9px] sm:text-[10px] invisible select-none leading-none">
                  Free per day
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center py-2">
            <span className="text-lg sm:text-xl font-bold text-blue-500 uppercase tracking-tighter">Coming Soon</span>
          </div>
        )}

        {/* Savings Badge slot with fixed height so buttons don't jump vertically */}
        <div className="h-5 flex items-center justify-center mt-1">
          {savingsBadgeText ? (
            <span className="inline-block bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 text-[9px] sm:text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wide leading-none">
              {savingsBadgeText}
            </span>
          ) : null}
        </div>
      </div>

      <div className="mb-5 sm:mb-6 flex flex-col items-center">
        <button
          onClick={onSubscribe}
          disabled={isCurrent || loading || isComingSoon || isDisabledForDowngrade}
          className={cn(
            "w-full h-11 sm:h-12 py-2.5 sm:py-3 px-6 rounded-xl font-black text-xs sm:text-sm transition-all duration-300 shadow-md hover:shadow-lg active:scale-95 flex items-center justify-center",
            isPopular
              ? "bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white border-0 shadow-emerald-500/20"
              : "bg-[hsl(var(--card-bg))] text-foreground border border-[hsl(var(--card-border))] hover:bg-[hsl(var(--sidebar-hover))]",
            (isCurrent || isComingSoon || isDisabledForDowngrade) && "bg-muted text-muted-foreground border border-border cursor-not-allowed shadow-none"
          )}
        >
          {isCurrent ? 'Your current plan' : isComingSoon ? 'Coming Soon' : loading ? 'Processing...' : (isFree ? `Select ${name}` : `Upgrade to ${name}`)}
        </button>
      </div>

      <div className="flex-1 flex flex-col">
        {benefitData.listHeading && (
          <h4 className="text-[10px] sm:text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-2.5 sm:mb-4">
            {benefitData.listHeading}:
          </h4>
        )}
        <ul className="space-y-2 sm:space-y-3 mb-3 sm:mb-4">
          {benefitData.points.map((feature, i) => (
            <li key={i} className="flex items-start gap-2 sm:gap-2.5 text-[11px] sm:text-xs text-muted-foreground group">
              <CheckCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-primary flex-shrink-0 mt-0.5" />
              <span className="leading-snug">{feature.trim()}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
